import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChatCircleText,
  CheckCircle,
  Clock,
  PaperPlaneTilt,
  Warning,
} from '@phosphor-icons/react';

import {
  FEEDBACK_COMMENT_BODY_MAX,
  getFeedback,
  listFeedbackComments,
  postFeedbackComment,
  type FeedbackComment,
  type FeedbackStatus,
  type FeedbackTicket,
} from '@/api/feedback';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormSkeleton } from '@/components/ui/form-skeleton';

// FeedbackDetail — single ticket view with the full conversation thread and
// a textarea to add a reply. Reached from the list page; the conversation
// mirrors what's on GitHub (maintainer replies come in via the webhook;
// user replies go out via the App's AddComment). Refresh-on-mount only —
// new maintainer comments still ping the bell, the user clicks through to
// reload, no polling.

const COMMENT_MIN = 1;

export function FeedbackDetail() {
  const { slug = '', ticketID = '' } = useParams();
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);

  const ticketQuery = useQuery<FeedbackTicket>({
    queryKey: ['feedback', slug, ticketID],
    queryFn: () => getFeedback(token, slug, ticketID),
    enabled: !!token && !!slug && !!ticketID,
  });

  const commentsQuery = useQuery({
    queryKey: ['feedback', slug, ticketID, 'comments'],
    queryFn: () => listFeedbackComments(token, slug, ticketID),
    enabled: !!token && !!slug && !!ticketID,
  });

  const sendMutation = useMutation({
    mutationFn: (body: string) => postFeedbackComment(token, slug, ticketID, body),
    onSuccess: () => {
      setDraft('');
      setSendError(null);
      void queryClient.invalidateQueries({
        queryKey: ['feedback', slug, ticketID, 'comments'],
      });
    },
    onError: (err: Error) => {
      setSendError(err.message || 'unknown');
    },
  });

  // Refresh the thread once when the page mounts so a navigation back from
  // elsewhere doesn't paint stale data even within the React Query cache TTL.
  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: ['feedback', slug, ticketID, 'comments'],
    });
  }, [queryClient, slug, ticketID]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length < COMMENT_MIN || trimmed.length > FEEDBACK_COMMENT_BODY_MAX) {
      setSendError(
        t('settings.feedback.comments.lengthError', {
          min: COMMENT_MIN,
          max: FEEDBACK_COMMENT_BODY_MAX,
        }),
      );
      return;
    }
    setSendError(null);
    sendMutation.mutate(trimmed);
  }

  const ticket = ticketQuery.data;
  const comments = commentsQuery.data?.comments ?? [];

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <div>
        <Link
          to={`/orgs/${slug}/settings/feedback`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-foreground/70 hover:text-foreground"
        >
          <ArrowLeft size={16} weight="bold" />
          {t('settings.feedback.detail.back')}
        </Link>
      </div>

      {ticketQuery.isLoading && <FormSkeleton rows={4} action={false} />}

      {ticket && (
        <>
          <header className="space-y-2 rounded-3xl border border-border/60 bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={ticket.status} />
              {ticket.kind && (
                <Badge variant="outline" className="text-xs">
                  {t(`settings.feedback.kind.${ticket.kind}`, ticket.kind)}
                </Badge>
              )}
              {ticket.severity && (
                <Badge variant="outline" className="text-xs">
                  {t(`settings.feedback.severity.${ticket.severity}`, ticket.severity)}
                </Badge>
              )}
            </div>
            <h1 className="break-words text-xl font-bold tracking-tight text-foreground">
              {ticket.title}
            </h1>
            <p className="whitespace-pre-wrap break-words text-sm text-foreground/80">
              {ticket.body}
            </p>
            <p className="text-xs text-foreground/60">
              {formatDateTime(ticket.created_at, i18n.language)}
              {ticket.github_issue_number != null && <> · #{ticket.github_issue_number}</>}
            </p>
          </header>

          <section className="space-y-3">
            <h2 className="text-base font-semibold tracking-tight text-foreground">
              {t('settings.feedback.comments.title')}
            </h2>
            {commentsQuery.isLoading && (
              <p className="text-sm text-foreground/60">{t('settings.feedback.list.loading')}</p>
            )}
            {!commentsQuery.isLoading && comments.length === 0 && (
              <p className="rounded-2xl border border-border/60 bg-card p-4 text-center text-sm text-foreground/60">
                {t('settings.feedback.comments.empty')}
              </p>
            )}
            {comments.length > 0 && (
              <ul className="space-y-3">
                {comments.map((c) => (
                  <CommentBubble key={c.id} comment={c} locale={i18n.language} />
                ))}
              </ul>
            )}
          </section>

          {ticket.status !== 'closed' && ticket.github_issue_number != null && (
            <form
              onSubmit={onSubmit}
              className="space-y-3 rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
            >
              <label
                htmlFor="comment-body"
                className="text-sm font-semibold text-foreground"
              >
                {t('settings.feedback.comments.replyLabel')}
              </label>
              <textarea
                id="comment-body"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={FEEDBACK_COMMENT_BODY_MAX}
                rows={4}
                placeholder={t('settings.feedback.comments.replyPlaceholder')}
                className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lime"
                disabled={sendMutation.isPending}
              />
              {sendError && (
                <p
                  role="alert"
                  className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                >
                  {sendError}
                </p>
              )}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-foreground/60">
                  {t('settings.feedback.comments.replyHint')}
                </p>
                <Button
                  type="submit"
                  disabled={sendMutation.isPending || !draft.trim()}
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                  {sendMutation.isPending
                    ? t('settings.feedback.comments.sending')
                    : t('settings.feedback.comments.send')}
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function formatDateTime(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: FeedbackStatus }) {
  const { t } = useTranslation('common');
  const cfg: Record<
    FeedbackStatus,
    { icon: typeof CheckCircle; tone: 'default' | 'success' | 'warning' | 'destructive' }
  > = {
    pending: { icon: Clock, tone: 'default' },
    submitted: { icon: CheckCircle, tone: 'success' },
    open: { icon: ChatCircleText, tone: 'success' },
    closed: { icon: CheckCircle, tone: 'default' },
    failed: { icon: Warning, tone: 'destructive' },
  };
  const { icon: Icon, tone } = cfg[status];
  const toneClass = (() => {
    switch (tone) {
      case 'success':
        return 'border-lime/40 bg-lime/10 text-foreground';
      case 'warning':
        return 'border-orange-400/40 bg-orange-400/10 text-orange-700';
      case 'destructive':
        return 'border-destructive/40 bg-destructive/10 text-destructive';
      default:
        return 'border-border/60 bg-muted text-foreground/80';
    }
  })();
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-xs font-semibold ${toneClass}`}
    >
      <Icon size={12} weight="bold" />
      {t(`settings.feedback.status.${status}`)}
    </span>
  );
}

interface CommentBubbleProps {
  comment: FeedbackComment;
  locale: string;
}

function CommentBubble({ comment, locale }: CommentBubbleProps) {
  const { t } = useTranslation('common');
  const isUser = comment.author_kind === 'user';
  const bubbleClass = isUser
    ? 'ml-8 border-lime/40 bg-lime/5'
    : 'mr-8 border-border/60 bg-muted/30';
  return (
    <li className={`space-y-1 rounded-2xl border p-4 shadow-sm ${bubbleClass}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-foreground/80">
          {comment.author_label?.trim() ||
            t(`settings.feedback.comments.author.${comment.author_kind}`)}
        </p>
        <p className="text-xs text-foreground/60">{formatDateTime(comment.created_at, locale)}</p>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm text-foreground">{comment.body}</p>
      {comment.status === 'posting' && (
        <p className="flex items-center gap-1 text-xs text-foreground/60">
          <Clock size={12} weight="bold" />
          {t('settings.feedback.comments.statusPosting')}
        </p>
      )}
      {comment.status === 'failed' && (
        <p className="flex items-center gap-1 text-xs text-destructive">
          <Warning size={12} weight="bold" />
          {t('settings.feedback.comments.statusFailed')}
        </p>
      )}
    </li>
  );
}
